import FingerprintJS from '@fingerprintjs/fingerprintjs'
import type {Content} from '@prismicio/client'
import type {SliceComponentProps} from '@prismicio/react'
import type {FC} from 'react'
import {useEffect, useMemo, useReducer, useRef, useState} from 'react'
import {ToastProvider} from 'react-toast-notifications'
import {useGetConfig} from 'src/common/hooks/useGetConfig'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'
import {useRecaptcha} from 'src/common/hooks/useRecaptcha'
import {useHubspotForm} from 'src/Event/hooks/useHubspotForm'

import {Container} from '../../src/common/Container'
import {
  AUDIO_VOLUME,
  FALLBACK_QUESTION_POOL,
  GAME_STYLES,
  HOTJAR_ID,
  HOTJAR_SV,
  SCREEN_FADE_MS,
  STORAGE_KEYS,
} from './constants'
import type {FraudGameQuestionDocument} from './data'
import {getFraudGameSliceItems, resolveFraudGameQuestionPool} from './data'
import {mapSubmitEmailDataToHubspotFields} from './hubspot'
import type {Answer, GameAction, GameScreen, GameState, Question, QuestionAnswerTiming, SubmitEmailData} from './types'
import {createFraudGameAnalyticsId, useFraudGameAnalytics} from './useFraudGameAnalytics'
import type {LeaderboardData} from './useFraudGameApi'
import {useFraudGameApi} from './useFraudGameApi'
import {computeFinalScore, generateUsername, shuffleAndDraw} from './utils'

export type {SubmitEmailData} from './types'

import {Calculating} from './components/Calculating'
import {Intro} from './components/Intro'
import {InvoiceMasterData} from './components/InvoiceMasterData'
import {MuteButton} from './components/MuteButton'
import {PhaseTransition} from './components/PhaseTransition'
import {PhishingEmail} from './components/PhishingEmail'
import {QuestionScreen} from './components/QuestionScreen'
import {ResultsAlt as Results} from './components/ResultsAlt'

// ── Game state machine ────────────────────────────────────────────────────────

const createInitialState = (): GameState => ({
  screen: 'intro',
  questions: [],
  currentIndex: 0,
  answers: [],
  totalScore: 0,
  sessionStart: 0,
  playCount: 0,
  hasSubmittedEmail: false,
})

const reducer = (state: GameState, action: GameAction): GameState => {
  switch (action.type) {
    case 'START':
      return {
        ...state,
        screen: 'question',
        questions: action.questions,
        currentIndex: 0,
        answers: [],
        totalScore: 0,
        sessionStart: Date.now(),
        playCount: state.playCount + 1,
      }

    case 'ANSWER': {
      const answers = [...state.answers, action.answer]
      const totalScore = answers.reduce((sum, a) => sum + a.score, 0)
      const nextIndex = state.currentIndex + 1
      const isLast = nextIndex >= state.questions.length

      const currentPhase = state.questions[state.currentIndex]?.phase
      const nextPhase = state.questions[nextIndex]?.phase
      const isPhaseTransition = !isLast && currentPhase !== nextPhase

      let screen: GameScreen

      if (isLast) {
        screen = 'calculating'
      } else if (isPhaseTransition) {
        screen = 'phase-transition'
      } else {
        screen = 'question'
      }

      return {
        ...state,
        answers,
        totalScore,
        currentIndex: isLast || isPhaseTransition ? state.currentIndex : nextIndex,
        screen,
      }
    }

    case 'CONTINUE_AFTER_TRANSITION':
      return {...state, screen: 'question', currentIndex: state.currentIndex + 1}

    case 'SHOW_CALCULATING':
      return {...state, screen: 'calculating'}

    case 'SHOW_RESULTS':
      return {...state, screen: 'results'}

    case 'GO_BACK': {
      const prevAnswers = state.answers.slice(0, -1)
      return {
        ...state,
        screen: 'question',
        answers: prevAnswers,
        totalScore: prevAnswers.reduce((sum, a) => sum + a.score, 0),
        currentIndex: state.questions.length - 1,
      }
    }

    case 'SUBMIT_EMAIL':
      return {...state, hasSubmittedEmail: true}

    case 'RESET':
      return {
        ...createInitialState(),
        playCount: state.playCount,
        hasSubmittedEmail: state.hasSubmittedEmail,
      }

    default:
      return state
  }
}

// ── Slice component ───────────────────────────────────────────────────────────

type FraudGameContext = {
  fraudGameQuestions?: FraudGameQuestionDocument[]
}

export type FraudGameProps = SliceComponentProps<Content.FraudGameSlice, FraudGameContext>

const FraudGameContent: FC<FraudGameProps> = ({slice, context}) => {
  const [state, dispatch] = useReducer(reducer, createInitialState())
  const audioRef = useRef<HTMLAudioElement>(null)
  const [muted, setMuted] = useState(true)
  const [audioStarted, setAudioStarted] = useState(false)
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardData | null>(null)
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [sessionBlockError, setSessionBlockError] = useState<string | null>(null)
  const [finalScore, setFinalScore] = useState<{preciseScore: number; displayScore: number} | null>(null)
  const [gameRunId, setGameRunId] = useState('')
  const introViewedKeysRef = useRef<Set<string>>(new Set())
  const questionViewedKeysRef = useRef<Set<string>>(new Set())
  const detailsPromptViewedKeysRef = useRef<Set<string>>(new Set())
  const leaderboardViewedKeysRef = useRef<Set<string>>(new Set())

  const {startSession, createPlayer, getLeaderboard, createReferral, logGame} = useFraudGameApi()
  const recaptcha = useRecaptcha()
  const config = useGetConfig()
  const domainCountry = useGetDomainCountry()
  const hubspotFormId = slice.primary.hubspot_form_id

  const {submit: submitHubspotForm} = useHubspotForm({
    formId: hubspotFormId,
    pageUid: 'fraud-game',
    trackingLocation: 'fraud-game-join-leaderboard',
    trackDataType: 'fraud-game',
  })

  const [playerId, setPlayerId] = useState(() => {
    if (typeof window === 'undefined') {
      return ''
    }

    const existingId = localStorage.getItem(STORAGE_KEYS.PLAYER_ID)

    if (existingId) {
      return existingId
    }

    const newId = createFraudGameAnalyticsId()
    localStorage.setItem(STORAGE_KEYS.PLAYER_ID, newId)

    return newId
  })

  const [username, setUsername] = useState(() => {
    if (typeof window === 'undefined') {
      return 'Player'
    }

    const storedUsername = localStorage.getItem(STORAGE_KEYS.USERNAME) ?? 'Player'
    return storedUsername
  })

  const [referrerId] = useState(() => {
    if (typeof window === 'undefined') {
      return null
    }

    const ref = new URLSearchParams(window.location.search).get('ref')
    return ref
  })

  const fingerprintRef = useRef<string | undefined>(undefined)

  // Load browser fingerprint for anti-fraud session tracking
  useEffect(() => {
    FingerprintJS.load()
      .then((fp) => fp.get())
      .then((result) => {
        fingerprintRef.current = result.visitorId
      })
      .catch(() => {})
  }, [])

  const analytics = useFraudGameAnalytics({
    playerId,
    gameRunId,
    referrerId,
    attemptNumber: state.playCount,
  })

  // Track intro reach once per displayed intro screen, including referred visits
  useEffect(() => {
    if (state.screen !== 'intro') {
      return
    }

    const key = `${state.playCount}:${referrerId ?? 'direct'}`

    if (introViewedKeysRef.current.has(key)) {
      return
    }

    introViewedKeysRef.current.add(key)
    analytics.trackIntroViewed()

    if (referrerId) {
      analytics.trackTryYourselfViewed()
    }
  }, [analytics, referrerId, state.playCount, state.screen])

  // Track question exposure once per game run and question index
  useEffect(() => {
    if (state.screen !== 'question') {
      return
    }

    const question = state.questions[state.currentIndex]

    if (!question) {
      return
    }

    const key = `${gameRunId}:${state.currentIndex}:${question.id}`

    if (questionViewedKeysRef.current.has(key)) {
      return
    }

    questionViewedKeysRef.current.add(key)

    analytics.trackQuestionViewed({
      question,
      questionNumber: state.currentIndex + 1,
      totalQuestions: state.questions.length,
    })
  }, [analytics, gameRunId, state.currentIndex, state.questions, state.screen])

  // Track post-quiz details prompt exposure before the leaderboard is unlocked
  useEffect(() => {
    if (state.screen !== 'results' || state.hasSubmittedEmail) {
      return
    }

    const key = gameRunId || `attempt-${state.playCount}`

    if (detailsPromptViewedKeysRef.current.has(key)) {
      return
    }

    detailsPromptViewedKeysRef.current.add(key)
    analytics.trackDetailsPromptViewed({score: state.totalScore})
  }, [analytics, gameRunId, state.hasSubmittedEmail, state.playCount, state.screen, state.totalScore])

  // Track leaderboard reach after a successful details submission or returning-player preload
  useEffect(() => {
    if (state.screen !== 'results' || !state.hasSubmittedEmail || !leaderboardData) {
      return
    }

    const key = `${gameRunId || `attempt-${state.playCount}`}:${leaderboardData.currentPlayer?.position ?? 0}`

    if (leaderboardViewedKeysRef.current.has(key)) {
      return
    }

    leaderboardViewedKeysRef.current.add(key)

    analytics.trackLeaderboardViewed({
      score: state.totalScore,
      rank: leaderboardData.currentPlayer?.position ?? 0,
      totalPlayers: leaderboardData.totalPlayers,
    })
  }, [analytics, gameRunId, leaderboardData, state.hasSubmittedEmail, state.playCount, state.screen, state.totalScore])

  // Compute score during the calculating screen and prefetch leaderboard for returning players
  useEffect(() => {
    if (state.screen !== 'calculating' || finalScore) {
      return
    }

    const score = computeFinalScore(state.answers, state.questions)

    setFinalScore(score)

    if (!localStorage.getItem(STORAGE_KEYS.USERNAME)) {
      const newUsername = generateUsername()
      localStorage.setItem(STORAGE_KEYS.USERNAME, newUsername)
      setUsername(newUsername)
    }

    void logGame({
      playerId,
      score: score.preciseScore,
      fingerprint: fingerprintRef.current,
    }).catch(() => {})

    const storedEmail = localStorage.getItem(STORAGE_KEYS.EMAIL)

    if (storedEmail) {
      recaptcha
        .execute('create_player')
        .then((captchaToken) =>
          createPlayer({
            id: playerId,
            email: storedEmail,
            username,
            score: score.preciseScore,
            questionIds: state.questions.map((q) => q.id),
            captchaToken,
          }),
        )
        .catch(() => {})
        .then(() => getLeaderboard(playerId))
        .then((data) => {
          setLeaderboardData(data)
          dispatch({type: 'SUBMIT_EMAIL'})
        })
        .catch(() => {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire during calculating screen
  }, [state.screen])

  const handleSubmitEmail = async (data: SubmitEmailData) => {
    setIsSubmittingEmail(true)
    setEmailError(null)

    try {
      const displayName = localStorage.getItem(STORAGE_KEYS.USERNAME) ?? username
      const captchaToken = await recaptcha.execute('create_player')

      const {playerId: confirmedId, username: confirmedUsername} = await createPlayer({
        id: playerId,
        email: data.email,
        username: displayName,
        score: finalScore?.preciseScore ?? state.totalScore,
        questionIds: state.questions.map((q) => q.id),
        captchaToken,
      })

      const finalUsername = confirmedUsername ?? displayName
      localStorage.setItem(STORAGE_KEYS.PLAYER_ID, confirmedId)
      localStorage.setItem(STORAGE_KEYS.EMAIL, data.email)
      localStorage.setItem(STORAGE_KEYS.USERNAME, finalUsername)
      setUsername(finalUsername)

      setPlayerId(confirmedId)

      if (referrerId) {
        await createReferral({referredEmail: data.email, refererId: referrerId})
      }

      const leaderboard = await getLeaderboard(confirmedId)
      setLeaderboardData(leaderboard)

      analytics.trackCompetitionEntrySubmitted({
        score: state.totalScore,
        playerId: confirmedId,
        email: data.email,
        marketingOptIn: data.marketingOptIn,
        context: {playerId: confirmedId},
      })

      dispatch({type: 'SUBMIT_EMAIL'})

      const hubspotFields = mapSubmitEmailDataToHubspotFields(data, domainCountry)

      if (hubspotFormId && hubspotFields && config.ENV === 'production') {
        void submitHubspotForm(hubspotFields)
      }
    } catch (err) {
      const message =
        err instanceof Error && 'response' in err
          ? ((err as {response?: {data?: {message?: string}}}).response?.data?.message ?? 'Something went wrong.')
          : 'Something went wrong.'

      setEmailError(message)
      analytics.trackDetailsSubmitFailed({score: state.totalScore, errorMessage: message})

      throw err
    } finally {
      setIsSubmittingEmail(false)
    }
  }

  // Load Hotjar tracking for the fraud game only
  useEffect(() => {
    const w = window as unknown as Record<string, unknown>

    w.hj =
      w.hj ||
      function (...args: unknown[]) {
        ;((w.hj as {q?: unknown[]}).q = (w.hj as {q?: unknown[]}).q || []).push(args)
      }

    w._hjSettings = {hjid: HOTJAR_ID, hjsv: HOTJAR_SV}
    const script = document.createElement('script')
    script.async = true
    script.src = `https://static.hotjar.com/c/hotjar-${HOTJAR_ID}.js?sv=${HOTJAR_SV}`
    document.head.appendChild(script)

    return () => {
      script.remove()
    }
  }, [])

  const pool = useMemo(() => {
    const prismicPool = resolveFraudGameQuestionPool(
      getFraudGameSliceItems(slice.items as unknown[]),
      context?.fraudGameQuestions,
    )

    return prismicPool ?? FALLBACK_QUESTION_POOL
  }, [context?.fraudGameQuestions, slice.items])

  // ── Fade transitions between screens ──
  const [visibleScreen, setVisibleScreen] = useState<GameScreen>(state.screen)
  const [opacity, setOpacity] = useState(1)
  const [fromIntro, setFromIntro] = useState(false)
  const prevScreenRef = useRef<GameScreen>(state.screen)

  // Fade out/in when switching between game screens
  useEffect(() => {
    if (state.screen === prevScreenRef.current) {
      return
    }

    const prev = prevScreenRef.current
    prevScreenRef.current = state.screen

    if (state.screen === 'phase-transition' || prev === 'phase-transition') {
      setVisibleScreen(state.screen)
      return
    }

    if (prev === 'intro') {
      setFromIntro(true)
      setVisibleScreen(state.screen)
      return
    }

    setOpacity(0)

    const t = setTimeout(() => {
      setVisibleScreen(state.screen)
      setOpacity(1)
    }, SCREEN_FADE_MS)

    return () => clearTimeout(t)
  }, [state.screen])

  const startAudio = () => {
    if (audioStarted) {
      return
    }

    const isMobile = window.innerWidth < 768
    setAudioStarted(true)

    if (isMobile) {
      setMuted(true)
      return
    }

    setMuted(false)

    if (audioRef.current) {
      audioRef.current.volume = AUDIO_VOLUME
      audioRef.current.play().catch(() => setMuted(true))
    }
  }

  const toggleMute = () => {
    if (!audioRef.current) {
      return
    }

    if (muted) {
      if (!audioStarted) {
        setAudioStarted(true)
        audioRef.current.volume = AUDIO_VOLUME
      }

      audioRef.current
        .play()
        .then(() => setMuted(false))
        .catch(() => {})
    } else {
      audioRef.current.pause()
      setMuted(true)
    }
  }

  const handleStartGame = async () => {
    setSessionBlockError(null)

    const hasExistingEmail = typeof window !== 'undefined' && !!localStorage.getItem(STORAGE_KEYS.EMAIL)

    const startTrackedGame = (availablePool: Question[]) => {
      const selectedQuestions = shuffleAndDraw(availablePool)
      const nextGameRunId = createFraudGameAnalyticsId()
      const nextAttemptNumber = state.playCount + 1

      setGameRunId(nextGameRunId)
      dispatch({type: 'START', questions: selectedQuestions})

      analytics.trackGameStarted({
        questions: selectedQuestions,
        context: {
          gameRunId: nextGameRunId,
          playerId,
          attemptNumber: nextAttemptNumber,
        },
      })
    }

    try {
      const sendPlayerId = hasExistingEmail || referrerId ? playerId : undefined

      const {excludedQuestionIds} = await startSession({
        playerId: sendPlayerId,
        referrerId,
        fingerprint: fingerprintRef.current,
      })

      const filtered = excludedQuestionIds.length > 0 ? pool.filter((q) => !excludedQuestionIds.includes(q.id)) : pool
      startTrackedGame(filtered)
    } catch (err) {
      const status = (err as {response?: {status?: number}}).response?.status

      if (status === 429) {
        setSessionBlockError(
          'Unusual activity detected. For security reasons, your access has been temporarily restricted. Please try again in an hour.',
        )
      } else if (status === 403) {
        setSessionBlockError(
          'Your account has been permanently blocked due to suspicious activity. You are no longer eligible to participate in this game.',
        )
      } else {
        startTrackedGame(pool)
      }
    }
  }

  const handleAnswer = (value: string, score: number, correct: boolean, timing: QuestionAnswerTiming) => {
    const currentQuestion: Question = state.questions[state.currentIndex]

    const answer: Answer = {
      questionId: currentQuestion.id,
      value,
      score,
      correct,
      elapsedMs: timing.elapsedMs,
      remainingSeconds: timing.remainingSeconds,
      timedOut: timing.timedOut,
    }

    const nextAnswers = [...state.answers, answer]
    const totalScore = nextAnswers.reduce((sum, item) => sum + item.score, 0)
    const isLast = state.currentIndex + 1 >= state.questions.length

    if (isLast) {
      const durationMs = state.sessionStart ? Date.now() - state.sessionStart : 0

      analytics.trackQuizCompleted({
        score: totalScore,
        answers: nextAnswers,
        questions: state.questions,
        durationMs,
      })
    }

    dispatch({
      type: 'ANSWER',
      answer,
    })
  }

  const handleAnswerLocked = (value: string, score: number, correct: boolean, timing: QuestionAnswerTiming) => {
    const currentQuestion: Question = state.questions[state.currentIndex]

    analytics.trackQuestionAnswered({
      question: currentQuestion,
      questionNumber: state.currentIndex + 1,
      totalQuestions: state.questions.length,
      answerValue: value,
      score,
      correct,
      elapsedMs: timing.elapsedMs,
      remainingSeconds: timing.remainingSeconds,
      timedOut: timing.timedOut,
    })
  }

  const screen: GameScreen = visibleScreen

  return (
    <Container
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative flex min-h-screen flex-col !bg-moss-black"
    >
      {/* Game animation styles */}
      <style dangerouslySetInnerHTML={{__html: GAME_STYLES}} />

      {/* Background audio */}
      <audio ref={audioRef} src="/audio/fraud-game/opening-the-case.mp3" loop preload="auto" />

      {/* Mute/unmute toggle — top-right on non-intro screens */}
      {screen !== 'intro' && (
        <MuteButton
          muted={muted}
          onToggle={toggleMute}
          className="absolute right-6 top-[100px] z-50 md:right-[30px] md:top-20"
        />
      )}

      {/* Fade wrapper for screen transitions */}
      <div
        className="min-h-screen transition-opacity duration-700 ease-in-out"
        style={{
          opacity,
          ...(fromIntro ? {animation: 'fade-in 800ms ease-out forwards'} : {}),
        }}
        onAnimationEnd={() => setFromIntro(false)}
      >
        {screen === 'intro' && (
          <Intro onStart={handleStartGame} onFirstInteraction={startAudio} muted={muted} onToggleMute={toggleMute} />
        )}

        {screen === 'question' &&
          state.questions.length > 0 &&
          (() => {
            const currentQuestion = state.questions[state.currentIndex]
            const key = `${state.currentIndex}-${currentQuestion?.id}`

            const sharedProps = {
              question: currentQuestion,
              questionNumber: state.currentIndex + 1,
              totalQuestions: state.questions.length,
              onAnswerLocked: handleAnswerLocked,
              onAnswer: handleAnswer,
            }

            if (currentQuestion?.type === 'phishing-email') {
              return <PhishingEmail key={key} {...sharedProps} />
            }

            if (currentQuestion?.type === 'invoice-master-data') {
              return <InvoiceMasterData key={key} {...sharedProps} />
            }

            return <QuestionScreen key={key} {...sharedProps} />
          })()}

        {screen === 'phase-transition' &&
          (() => {
            const completedPhase = (state.questions[state.currentIndex]?.phase ?? 1) as 1 | 2
            return (
              <PhaseTransition
                completedPhase={completedPhase}
                questionNumber={state.currentIndex + 1}
                totalQuestions={state.questions.length}
                onComplete={() => dispatch({type: 'CONTINUE_AFTER_TRANSITION'})}
              />
            )
          })()}

        {screen === 'calculating' && (
          <Calculating
            onComplete={() => {
              dispatch({type: 'SHOW_RESULTS'})
            }}
          />
        )}

        {screen === 'results' && (
          <Results
            score={finalScore?.displayScore ?? state.totalScore}
            answers={state.answers}
            questions={state.questions}
            playCount={state.playCount}
            sessionStart={state.sessionStart}
            hasSubmittedEmail={state.hasSubmittedEmail}
            username={username}
            playerId={playerId}
            leaderboardData={leaderboardData}
            isSubmittingEmail={isSubmittingEmail}
            emailError={emailError}
            onPlayAgain={() => {
              analytics.trackReplayClicked({previousScore: state.totalScore})
              setFinalScore(null)
              setGameRunId('')
              dispatch({type: 'RESET'})
            }}
            onSubmitEmail={handleSubmitEmail}
            onDetailsFormOpen={() => analytics.trackDetailsFormOpened({score: state.totalScore})}
            onShareModalOpen={() => analytics.trackShareModalOpened({score: state.totalScore})}
            onShareClick={(shareType, shareUrl) =>
              analytics.trackShareClicked({shareType, shareUrl, score: state.totalScore})
            }
            competitionEndDate={(slice.primary as {competition_end_date?: string | null}).competition_end_date}
          />
        )}
      </div>

      {sessionBlockError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-moss-black/70 backdrop-blur-sm">
          <div className="mx-4 w-full max-w-[400px] rounded-2xl border border-grey-900 bg-[#171717] p-6 text-center shadow-2xl">
            <p className="text-16 font-medium text-grey-110">{sessionBlockError}</p>
          </div>
        </div>
      )}
    </Container>
  )
}

const FraudGame: FC<FraudGameProps> = (props) => (
  <ToastProvider autoDismiss={5000}>
    <FraudGameContent {...props} />
  </ToastProvider>
)

export default FraudGame
